import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
    DataGrid, GridRowModes, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector,
    GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter,
    GridActionsCellItem,
    GridRowEditStopReasons, GridColType
} from '@mui/x-data-grid';

import _uniqueId from 'lodash/uniqueId';
import { Button, Toolbar, Typography } from '@mui/material';
import { useEffect } from 'react';
import ATSnackbar from '../layout/ATSnackbar';
import { useState } from 'react';
import { DeleteIcon } from '@icons/material';

function EditToolbar(props) {
    const { setRows, setRowModesModel, newRowDefaultData } = props;

    const handleClick = () => {
        const id = 10000 + _uniqueId();
        setRows((oldRows) => [
            ...oldRows,
            { ...newRowDefaultData, id, isNew: true },
        ]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
        }));
    };

    return (
        <Toolbar>
            <Tooltip title="Add record">
                <Button onClick={handleClick} variant='contained'>
                    <Typography fontSize="small">Add</Typography>
                </Button>
            </Tooltip>
        </Toolbar>
    );
}

// Make sure onAdd and onUpdate functions return a promise
// newRowDefaultData: When clicked add new row, this data will be used to fill the new row
export default function CRUDDataTable({ title, columnsData, initialRows, newRowDefaultData, onAdd, onUpdate, onDelete, refreshData }) {
    const [rows, setRows] = useState(initialRows);

    const [alert, setAlert] = useState({
        message: '',
        open: false,
        severity: 'error'
    })

    useEffect(() => {
        setRows(initialRows)
    }, [initialRows]);

    const actionColumn =
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

            if (isInEditMode) {
                return [
                    <GridActionsCellItem
                        icon={<SaveIcon />}
                        label="Save"
                        material={{
                            sx: {
                                color: 'primary.main',
                            },
                        }}
                        onClick={handleSaveClick(id)}
                    />,
                    <GridActionsCellItem
                        icon={<CancelIcon />}
                        label="Cancel"
                        className="textPrimary"
                        onClick={handleCancelClick(id)}
                        color="inherit"
                    />,
                ];
            }

            const actionButtons = [
                <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit"
                    className="textPrimary"
                    onClick={handleEditClick(id)}
                    color="inherit"
                />
            ]

            if (onDelete) {
                actionButtons.push(
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                );
            }
            return actionButtons;
        },
    }

    const columns = [...columnsData, actionColumn]
    const [rowModesModel, setRowModesModel] = React.useState({});

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id) => async () => {
        onDelete(id).then(response => {
            if (response?.status >= 400) {
                console.log('Response not ok', response);
                setAlert({
                    message: 'Error deleting data: ' + response.message,
                    open: true,
                    severity: 'error'
                });
            }
            else {
                console.log('Response ok', response);
                setAlert({
                    message: 'Data deleted successfully',
                    open: true,
                    severity: 'success'
                });
                setRows(rows.filter((row) => row.id !== id));
            }
        }).catch(error => {
            console.log('ERROR', error.message);
            setAlert({
                message: 'Error deleting data: ' + error.message,
                open: true,
                severity: 'error'
            });
        });
        refreshData?.()
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow) => {
        try {
            await handleSaveOrUpdate(newRow);
            const updatedRow = { ...newRow, isNew: false };
            setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
            return updatedRow;
        } catch (error) {
            console.error('Error processing row update', error);
            return rows.find((row) => row.id === newRow.id);
        } finally {
            refreshData?.()
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    // Returns true if row is added/updated successfully, false otherwise
    const handleSaveOrUpdate = async (row) => {
        let rowUpdated = false;
        const result = row?.isNew ? onAdd(row) : onUpdate(row);
        try {
            const response = await result;
            if (response?.status >= 400) {
                console.log('Response not ok', response);
                setAlert({
                    message: 'Error saving data: ' + response.message,
                    open: true,
                    severity: 'error'
                });
            } else {
                console.log('Response ok', response);
                setAlert({
                    message: 'Data saved successfully',
                    open: true,
                    severity: 'success'
                });
                rowUpdated = true;
            }
        } catch (error) {
            console.log('ERROR', error.message);
            setAlert({
                message: 'Error saving data: ' + error.message,
                open: true,
                severity: 'error'
            });
        }
        return new Promise((resolve, reject) => {
            if (rowUpdated) {
                resolve(true);
            } else {
                reject(false);
            }
        });
    }

    const setOpenSnackbar = open => setAlert(prev => ({ ...prev, open }))

    return (
        <Box
            sx={{
                height: 500,
                width: '100%',
                '& .actions': {
                    color: 'text.secondary',
                },
                '& .textPrimary': {
                    color: 'text.primary',
                },
            }}
        >
            <ATSnackbar message={alert?.message} open={alert?.open} setOpen={setOpenSnackbar} severity={alert?.severity} />
            <DataGrid
                rows={rows || []}
                columns={columns || []}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                slots={{
                    toolbar: props => <>
                        <GridToolbarContainer style={{ justifyContent: 'space-around' }}>
                            <Typography variant='h5' align='center'>{title}</Typography>
                            <EditToolbar {...props} />
                            <GridToolbarColumnsButton />
                            <GridToolbarFilterButton />
                            <GridToolbarDensitySelector />
                            <GridToolbarExport />
                            <GridToolbarQuickFilter />
                        </GridToolbarContainer>
                    </>
                }}
                slotProps={{
                    toolbar: { setRows, setRowModesModel, newRowDefaultData },
                }}
                sx={{
                    bgcolor: 'white',
                    marginTop: 5
                }}
            />
        </Box>
    );
}
